<template>
  <div id="prizm">
    <template v-if="!props.readOnly">
      <div
        v-if="!state.selectedDemographic"
        class="search">
        <div class="input-container">
          <div class="icon">
            <IconSearch />
          </div>
          <input
            v-model="state.searchTerm"
            name="segmentSearch"
            class="form-control table-text-filter"
            type="text"
            :placeholder="capitalize($t('actions.searchForSegmentName'))" />
          <div class="delete">
            <div
              v-if="searchTerm != null && searchTerm?.length > 0"
              @click="clearSearchTerm">
              <IconCircleX />
            </div>
          </div>
        </div>

        <div class="dropdowns">
          <DropDown
            id="socialGroups"
            :is-multiselect="true"
            :is-mandatory="false"
            :selected-options="state.selectedSocialGroups"
            :excluded-options="state.excludedSocialGroups"
            :allow-exclusions="false"
            :options="socialGroups"
            :align-drop-down-to-right="true"
            option-label-property="label"
            option-track-by-property="id"
            :label="capitalize($t('names.socialGroup', { n: 2 }))"
            :label-placeholder="capitalize($t('names.socialGroup', { n: 2 }))"
            :show-floating-label="
              Boolean(state.selectedSocialGroups?.length) || Boolean(state.excludedSocialGroups?.length)
            "
            :show-popup-header="false"
            :enable-search="true"
            :search-placeholder="capitalize($t('actions.search'))"
            @selection-changed="onSelectedSocialGroupChange" />

          <DropDown
            id="lifeStages"
            :is-multiselect="true"
            :is-mandatory="false"
            :selected-options="state.selectedLifeStages"
            :excluded-options="state.excludedLifeStages"
            :allow-exclusions="false"
            :options="lifeStages"
            :align-drop-down-to-right="true"
            option-label-property="label"
            option-track-by-property="id"
            :label="capitalize($t('names.lifeStage', { n: 2 }))"
            :label-placeholder="capitalize($t('names.lifeStage', { n: 2 }))"
            :show-floating-label="
              Boolean(state.selectedLifeStages?.length) || Boolean(state.excludedLifeStages?.length)
            "
            :show-popup-header="false"
            :enable-search="true"
            :search-placeholder="capitalize($t('actions.search'))"
            @selection-changed="onSelectedLifeStagesChange" />
        </div>
      </div>

      <div
        v-if="state.demographics?.length > 0 && !state.selectedDemographic"
        ref="prizmSegmentsRef"
        class="prizm-segments">
        <PrizmSegmentCard
          v-for="demographic in displayedDemographics"
          :key="demographic.id"
          :demographic="demographic"
          @segment-selected="segmentSelected" />
      </div>

      <Pagination
        v-if="state.demographics?.length && !state.selectedDemographic"
        v-model="state.pageIndex"
        size="md"
        variant="primary"
        :total-rows="totalCount"
        :per-page="state.pageSize" />
    </template>

    <div
      v-if="state.selectedDemographic"
      class="prizm-selected-segment">
      <PrizmSegmentCard
        :key="state.selectedDemographic.code"
        :demographic="state.selectedDemographic"
        :is-selected="true" />
    </div>

    <div
      v-if="state.isLoading"
      class="loading">
      <LoaderSmall /> {{ $t('states.loading') }}...
    </div>

    <div
      v-if="filteredDemographics?.length === 0 && !state.isLoading"
      class="empty">
      <IconSad />
      {{ $t('messages.noPredefinedSegments') }}
    </div>

    <button
      v-if="state.selectedDemographic && !props.readOnly"
      class="btn btn-sm btn-default"
      @click="clearSegment">
      {{ $t('messages.changeAudience') }}
    </button>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onMounted, onUnmounted, reactive, watch } from 'vue';

  import { DropDown } from '@/common/components/controls';
  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import IconSad from '@/common/icons/colored/Sad.vue';
  import IconCircleX from '@/common/icons/monochrome/CircleX.vue';
  import IconSearch from '@/common/icons/monochrome/Search.vue';
  import { Demographic, Filter, FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models';
  import PrizmData from '@/org-app/components/audienceLogics/data/prizm/PrizmData';
  import PrizmSegmentCard from '@/org-app/components/audienceLogics/PrizmSegmentCard.vue';
  import Pagination from '@/org-app/components/controls/Pagination.vue';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import { mapActions } from '@/utils/StateMapping';

  interface Option {
    id: string;
    label: string;
  }

  interface State {
    demographics: Demographic[];
    isLoading: boolean;
    searchTerm: string;
    totalCount: number;
    pageSize: number;
    pageIndex: number;
    selectedDemographic: Demographic;
    selectedSocialGroups: Option[];
    selectedLifeStages: Option[];
    excludedSocialGroups: Option[];
    excludedLifeStages: Option[];
  }

  // State
  const state = reactive<State>({
    demographics: [],
    isLoading: false,
    searchTerm: null,
    totalCount: 0,
    pageSize: 10,
    pageIndex: 1,
    selectedDemographic: null,
    selectedSocialGroups: [],
    selectedLifeStages: [],
    excludedSocialGroups: [],
    excludedLifeStages: [],
  });
  const props = defineProps({
    selectedDemographicId: { type: Number, default: null },
    readOnly: { type: Boolean, default: false },
  });

  // Store actions
  const { getDemographics } = mapActions();

  // Emit
  const emit = defineEmits<{ (e: 'onPrizmSegmentSelected', demographic: Demographic): void }>();

  // Computed
  const searchTerm = computed<string>(() => state.searchTerm);

  const isFilterUsed = computed(
    () =>
      state.searchTerm != null ||
      state.selectedSocialGroups?.length > 0 ||
      state.excludedSocialGroups?.length > 0 ||
      state.selectedLifeStages?.length > 0 ||
      state.excludedLifeStages?.length > 0,
  );

  const filteredDemographics = computed<Demographic[]>(() => {
    let demographics = [...state.demographics];

    if (state.searchTerm) {
      demographics = demographics.filter((demo) => demo.name.toLowerCase().includes(state.searchTerm.toLowerCase()));
    }

    if (state.selectedSocialGroups?.length) {
      demographics = demographics.filter(
        (demo) =>
          state.selectedSocialGroups
            .map((s) => s.label)
            .indexOf(AudienceSegmentUtils.getPrizmDetails(demo).socialGroup) > -1,
      );
    }

    if (state.excludedSocialGroups?.length) {
      demographics = demographics.filter(
        (demo) =>
          state.excludedSocialGroups
            .map((s) => s.label)
            .indexOf(AudienceSegmentUtils.getPrizmDetails(demo).socialGroup) === -1,
      );
    }

    if (state.selectedLifeStages?.length) {
      demographics = demographics.filter(
        (demo) =>
          state.selectedLifeStages.map((s) => s.label).indexOf(AudienceSegmentUtils.getPrizmDetails(demo).lifeStage) >
          -1,
      );
    }

    if (state.excludedLifeStages?.length) {
      demographics = demographics.filter(
        (demo) =>
          state.excludedLifeStages.map((s) => s.label).indexOf(AudienceSegmentUtils.getPrizmDetails(demo).lifeStage) ===
          -1,
      );
    }

    return demographics;
  });
  const totalCount = computed<number>(() => filteredDemographics.value.length);
  const displayedDemographics = computed<Demographic[]>(() => {
    const end = state.pageIndex * state.pageSize;
    const start = end === 0 ? state.pageSize : end - state.pageSize;
    return filteredDemographics.value.slice(start, end);
  });
  const socialGroups = computed<{ id: string; label: string }[]>(() =>
    Object.keys(PrizmData.socialGroups).map((key) => ({ id: key, label: PrizmData.socialGroups[key] })),
  );
  const lifeStages = computed<{ id: string; label: string }[]>(() =>
    Object.keys(PrizmData.lifeStages).map((key) => ({ id: key, label: PrizmData.lifeStages[key] })),
  );

  // Methods
  const fetchDemographics = async (): Promise<void> => {
    state.isLoading = true;

    const filterCollection = [new Filter('code', [AudienceSegmentUtils.prizmId], FilterComparisonOperator.STARTS_WITH)];

    if (state.searchTerm) {
      filterCollection.push(new Filter('name', [state.searchTerm], FilterComparisonOperator.LIKE));
    }

    const queryParams = new QueryParams(
      PrizmData.segmentDetails.length,
      state.pageIndex - 1,
      new FilterCollection(filterCollection),
    );
    queryParams.showLoader = false;
    const response = await queryParams.run(getDemographics);
    state.demographics = response.items;
    state.totalCount = response.totalCount;
    state.isLoading = false;
  };
  const clearSegment = (): void => {
    state.selectedDemographic = null;
    state.pageIndex = 1;
    emit('onPrizmSegmentSelected', null);
  };
  const clearSearchTerm = (): void => {
    state.searchTerm = null;
  };
  const segmentSelected = (demographic: Demographic): void => {
    state.selectedDemographic = demographic;
    state.searchTerm = null;
    state.selectedSocialGroups = [];
    state.excludedSocialGroups = [];
    state.selectedLifeStages = [];
    state.excludedLifeStages = [];

    emit('onPrizmSegmentSelected', demographic);
  };
  const onSelectedSocialGroupChange = (val: { included: Option[]; excluded: Option[] }): void => {
    state.selectedSocialGroups = val.included;
    state.excludedSocialGroups = val.excluded;
  };
  const onSelectedLifeStagesChange = (val: { included: Option[]; excluded: Option[] }): void => {
    state.selectedLifeStages = val.included;
    state.excludedLifeStages = val.excluded;
  };
  const determineNumberOfSlotsAvailable = () => {
    // We want to avoid having white space because we load less item than our resolution allow us to
    // The function follows the media query grid defined below in the scss * 2 (to get 2 lines each time if possible)
    // TODO would be nice to link this and the css together so we can change one without changing the other
    const width = document.body.offsetWidth;
    let elements = 0;
    switch (true) {
      case width >= 3000:
        elements = 12;
        break;
      case width >= 2750:
        elements = 11;
        break;
      case width >= 2500:
        elements = 10;
        break;
      case width >= 2250:
        elements = 9;
        break;
      case width >= 2000:
        elements = 8;
        break;
      case width >= 1750:
        elements = 7;
        break;
      case width >= 1550:
        elements = 6;
        break;
      case width >= 1375:
        elements = 5;
        break;
      case width >= 1200:
        elements = 4;
        break;
      case width >= 1000:
        elements = 3;
        break;
      case width >= 768:
        elements = 2;
        break;
      default:
        elements = 1;
    }

    state.pageSize = elements * 2;
  };

  // if one of the filter is used, reset the page index back to the first page
  watch(isFilterUsed, (value) => {
    if (value) {
      state.pageIndex = 1;
    }
  });

  // Lifecycle hooks
  onMounted(async (): Promise<void> => {
    window.addEventListener('resize', determineNumberOfSlotsAvailable);
    await fetchDemographics();
    determineNumberOfSlotsAvailable();

    if (props.selectedDemographicId) {
      state.selectedDemographic = state.demographics.find((demo) => demo.id === props.selectedDemographicId);
    }
  });
  onUnmounted(() => {
    window.removeEventListener('resize', determineNumberOfSlotsAvailable);
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #prizm {
    position: relative;
  }

  .loading {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    background: rgba(255, 255, 255, 0.7);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .search {
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;

    .input-container {
      position: relative;
      margin-bottom: 1rem;

      input {
        width: 30rem;
      }

      .delete {
        display: flex;
        align-items: center;
        position: absolute;
        right: 0.1rem;
        top: 0.9rem;
        margin-right: 0.6rem;

        div {
          display: flex;
          cursor: pointer;
        }

        svg {
          fill: #000;
          stroke: #000;
          opacity: 0.2;
          width: 1.6rem !important;
          height: 1.6rem !important;
        }
      }
    }

    .dropdowns {
      display: flex;

      .drop-down:last-child {
        margin-left: 1rem;
      }
    }
  }

  .prizm-segments {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-gap: 2rem;
    margin-bottom: 2rem;
  }

  .empty {
    display: flex;
    flex-direction: row;
    align-items: center;

    svg {
      height: 2rem;
      margin-right: 1rem;
    }
  }

  @media (max-width: 768px) {
    .prizm-segments {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
  }

  @media (min-width: 768px) {
    .prizm-segments {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (min-width: 1000px) {
    .prizm-segments {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (min-width: 1200px) {
    .prizm-segments {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  @media (min-width: 1375px) {
    .prizm-segments {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }
  }

  @media (min-width: 1550px) {
    .prizm-segments {
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }
  }

  @media (min-width: 1750px) {
    .prizm-segments {
      grid-template-columns: repeat(7, minmax(0, 1fr));
    }
  }

  @media (min-width: 2000px) {
    .prizm-segments {
      grid-template-columns: repeat(8, minmax(0, 1fr));
    }
  }

  @media (min-width: 2250px) {
    .prizm-segments {
      grid-template-columns: repeat(9, minmax(0, 1fr));
    }
  }

  @media (min-width: 2500px) {
    .prizm-segments {
      grid-template-columns: repeat(10, minmax(0, 1fr));
    }
  }

  @media (min-width: 2750px) {
    .prizm-segments {
      grid-template-columns: repeat(11, minmax(0, 1fr));
    }
  }

  @media (min-width: 3000px) {
    .prizm-segments {
      grid-template-columns: repeat(12, minmax(0, 1fr));
    }
  }
</style>
